package io.horion.cv.components.preview

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import io.horion.cv.api.datasource.cv.Model
import io.horion.cv.api.datasource.cv.OrionCV
import io.horion.cv.components.forms.ActivityAccordionContent
import io.horion.cv.components.forms.CenterInterestAccordionContent
import io.horion.cv.components.forms.CertificatAccordionContent
import io.horion.cv.components.forms.CompetenceAccordionContent
import io.horion.cv.components.forms.CourseAccordionContent
import io.horion.cv.components.forms.ExperienceAccordionContent
import io.horion.cv.components.forms.FormationAccordionContent
import io.horion.cv.components.forms.InternshipAccordionContent
import io.horion.cv.components.forms.LanguageAccordionContent
import io.horion.cv.components.forms.QualityAccordionContent
import io.horion.cv.components.forms.ReferenceAccordionContent
import io.horion.cv.models.AccordionSection
import io.horion.cv.models.ExtraSection
import io.horion.cv.models.accordionSections
import io.horion.cv.pages.cv.AccordionRow
import io.horion.cv.pages.cv.FormActionSection
import io.horion.cv.pages.cv.Separator
import io.horion.cv.styles.FormContentStyle
import io.horion.cv.styles.FormSectionContentStyle
import io.horion.cv.utils.Colors
import io.horion.cv.utils.Constants.INTER
import io.horion.cv.viewModel.CvViewModel
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

@Composable
fun CvPreviewPanel(
    data: OrionCV,
    model: Model,
    cvViewModel: CvViewModel,
    showZoomBar: Boolean = true,
    initialZoom: Double = 0.5,
) {
    var zoom by remember {
        mutableStateOf(initialZoom)
    }

    if (showZoomBar) {
        Div(
            attrs = {
                style {
                    property("position", "sticky")
                    property("top", "0")
                    property("z-index", "10")
                    property("background-color", Colors.LightGray.toString())
                    property("border-bottom", "1px solid #cccccc")
                    property("width", "100%")
                    property("display", "flex")
                    property("justify-content", "center")
                    property("align-items", "center")
                    property("padding", "8px 16px")
                    property("box-sizing", "border-box")
                    property("gap", "10px")
                }
            }) {
            // Bouton dézoom
            Box(
                modifier = Modifier
                    .size(32.px)
                    .borderRadius(6.px)
                    .backgroundColor(Colors.White)
                    .cursor(Cursor.Pointer)
                    .border(1.px, LineStyle.Solid, Colors.DarkGray)
                    .onClick { if (zoom > 0.3) zoom = (zoom - 0.05).coerceAtLeast(0.3) },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    "−",
                    modifier = Modifier
                        .fontSize(18.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Colors.Black)
                )
            }

            // Pourcentage
            SpanText(
                "${(zoom * 100).toInt()}%",
                modifier = Modifier
                    .fontSize(13.px)
                    .fontFamily(INTER)
                    .color(Colors.Black)
                    .width(45.px)
                    .styleModifier { property("text-align", "center") }
            )

            // Bouton zoom
            Box(
                modifier = Modifier
                    .size(32.px)
                    .borderRadius(6.px)
                    .backgroundColor(Colors.White)
                    .cursor(Cursor.Pointer)
                    .border(1.px, LineStyle.Solid, Colors.DarkGray)
                    .onClick { if (zoom < 1.0) zoom = (zoom + 0.05).coerceAtMost(1.0) },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    "+",
                    modifier = Modifier
                        .fontSize(18.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Colors.Black)
                )
            }

            // Bouton reset
            Box(
                modifier = Modifier
                    .borderRadius(6.px)
                    .backgroundColor(Colors.White)
                    .cursor(Cursor.Pointer)
                    .border(
                        1.px,
                        LineStyle.Solid,
                        Colors.DarkGray
                    )
                    .padding(
                        topBottom = 6.px,
                        leftRight = 10.px
                    )
                    .onClick {
                        zoom = initialZoom
                        cvViewModel.resetToBlank()
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    "Réinitialiser",
                    modifier = Modifier
                        .fontSize(12.px)
                        .fontFamily(INTER)
                        .color(Colors.Black)
                )
            }
        }
    }

    // Zone CV scalée
    val cvNaturalWidth = 794 // Largeur A4 standard en px (à 96 DPI)
    val cvNaturalHeight = 1123 // Hauteur A4 standard
    val scaledWidth = (cvNaturalWidth * zoom).toInt()
    val scaledHeight = (cvNaturalHeight * zoom).toInt()
    val marginBottom = scaledHeight - cvNaturalHeight

    // Wrapper global avec défilement sécurisé si besoin
    Div(
        attrs = {
            style {
                property("padding", "20px 0")
                property("display", "flex")
                property("justify-content", "center")
                property("width", "100%")
                property("max-width", "100vw")
                property("overflow-x", "auto") // Empeche le body de déborder
            }
        }) {
        // Conteneur réajusté à la largeur exacte du composant scalé
        Div(
            attrs = {
                style {
                    property("width", "${scaledWidth}px")
                    property("height", "${scaledHeight}px")
                    property("display", "flex")
                    property("justify-content", "center")
                    property("flex-shrink", "0")
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        property("transform", "scale($zoom)")
                        property("transform-origin", "top center")
                        property("margin-bottom", "${marginBottom}px")
                        property("filter", "drop-shadow(0 4px 24px rgba(0,0,0,0.18))")
                    }
                }) {
                when (model) {
                    Model.SIMPLE -> CvSimplePreview(data)
                    Model.CV2 -> CvTwoPreview(data)
                    Model.CV3 -> CvThreePreview(data)
                    Model.CV5 -> CvFivePreview(data)
                    Model.CV6 -> CvSixPreview(data)
                    Model.CV7 -> CvSevenPreview(data)
                    Model.CANADA -> CvCanadaPreview(data)
                    else -> CvSimplePreview(data)
                }
            }
        }
    }
}

@Composable
fun FormContent(
    cvViewModel: CvViewModel,
    openedSectionId: String?,
    activeExtraSections: List<ExtraSection>,
    onSectionOpen: (String?) -> Unit,
    onAddExtraSection: (ExtraSection) -> Unit,
    onOpenExtraSection: (ExtraSection) -> Unit,
    onRenameSection: (String, String) -> Unit,
    submitLabel: String = "Télécharger",
    onCustomSubmit: (() -> Unit)? = null
) {
    val extraAccordionSections = activeExtraSections.map { extra ->
        AccordionSection(
            id = extra.id,
            title = extra.title,
            content = {
                extra.content()
            }
        )
    }

    val allSections = accordionSections + extraAccordionSections

    Column(
        modifier = FormContentStyle.toModifier()
    ) {
        allSections.forEach { section ->
            val isOpen = section.id == openedSectionId

            AccordionRow(
                title = section.title,
                isOpen = isOpen,
                onClick = {
                    onSectionOpen(
                        if (isOpen) null else section.id
                    )
                },
                onRename = { newTitle ->
                    onRenameSection(section.id, newTitle)
                }
            )

            if (!isOpen) {
                Separator()
            }

            if (isOpen) {
                Box(
                    modifier = FormSectionContentStyle
                        .toModifier()
                        .styleModifier {
                            property(
                                "animation",
                                "fadeIn 0.3s ease"
                            )
                        }
                ) {
                    when (section.id) {
                        "personal_info",
                        "profil" -> {
                            section.content()
                        }

                        "education" -> {
                            FormationAccordionContent(
                                onComplete = {},
                                onAddFormationAndClose = {}
                            )
                        }

                        "experience" -> {
                            ExperienceAccordionContent(
                                onComplete = {},
                                onAddExperienceAndClose = {}
                            )
                        }

                        "skills" -> {
                            CompetenceAccordionContent(
                                onComplete = {},
                                onAddCompetenceAndClose = {}
                            )
                        }

                        "interest" -> {
                            CenterInterestAccordionContent(
                                onComplete = {},
                                onAddCompetenceAndClose = {}
                            )
                        }

                        "languages" -> {
                            LanguageAccordionContent(
                                onComplete = {},
                                onAddCompetenceAndClose = {}
                            )
                        }

                        "activite" -> {
                            ActivityAccordionContent(
                                onComplete = {},
                                onAddFormationAndClose = {}
                            )
                        }

                        "qualite" -> {
                            QualityAccordionContent(
                                onComplete = {},
                                onAddCompetenceAndClose = {}
                            )
                        }

                        "cours" -> {
                            CourseAccordionContent(
                                onComplete = {},
                                onAddCourseAndClose = {}
                            )
                        }

                        "certificats" -> {
                            CertificatAccordionContent(
                                onComplete = {},
                                onAddCertificatAndClose = {}
                            )
                        }

                        "references" -> {
                            ReferenceAccordionContent(
                                onComplete = {},
                                onAddReferenceAndClose = {}
                            )
                        }

                        "stage" -> {
                            InternshipAccordionContent(
                                onComplete = {},
                                onAddFormationAndClose = {}
                            )
                        }

                        else -> {
                            section.content()
                        }
                    }
                }

                Separator()
            }
        }

        FormActionSection(
            activeExtraSections = activeExtraSections,
            cvViewModel = cvViewModel,
            onAddExtraSection = onAddExtraSection,
            onOpenExtraSection = onOpenExtraSection,
            submitLabel = submitLabel,
            onCustomSubmit = onCustomSubmit
        )
    }
}